import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getApiUrl } from '../config/config';
import AdminLayout from '../components/AdminLayout';
import { Users, DollarSign, Shield, Star } from 'lucide-react';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: var(--background-dark);
  padding: 40px 20px;
`;

const AdminHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-bottom: 40px;
`;

const AdminTitle = styled.h1`
  color: var(--text-white);
  font-size: 2.5rem;
  font-weight: 700;
  font-family: var(--font-heading);
  margin-bottom: 16px;
`;

const AdminSubtitle = styled.p`
  color: var(--text-gray);
  font-size: 1.125rem;
  font-family: var(--font-body);
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto 40px;
`;

const StatCard = styled(motion.div)`
  background: var(--secondary-black);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  
  .stat-number {
    color: var(--primary-gold);
    font-size: 2.5rem;
    font-weight: 700;
    font-family: var(--font-heading);
    margin-bottom: 8px;
  }
  
  .stat-label {
    color: var(--text-gray);
    font-size: 1rem;
    font-family: var(--font-body);
  }
`;

const AdminGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 350px), 1fr));
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const AdminCard = styled(motion.div)`
  background: var(--secondary-black);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--primary-gold);
    transform: translateY(-4px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  
  svg {
    color: var(--primary-gold);
    width: 24px;
    height: 24px;
  }
  
  h3 {
    color: var(--text-white);
    font-size: 1.25rem;
    font-weight: 600;
    font-family: var(--font-heading);
  }
`;

const CardContent = styled.div`
  color: var(--text-gray);
  font-family: var(--font-body);
`;

const Tabs = styled.div`
  max-width: 1200px;
  margin: 0 auto 24px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const TabButton = styled.button`
  background: ${(p) => (p.$active ? 'var(--primary-gold)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--background-dark)' : 'var(--text-white)')};
  border: 1px solid var(--border-color);
  border-color: ${(p) => (p.$active ? 'var(--primary-gold)' : 'var(--border-color)')};
  padding: 10px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: all .2s ease;
  font-weight: bold;
`;

const Section = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Table = styled.div`
  width: 100%;
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow-x: auto;
`;

const THeader = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || '1fr 1fr'};
  background: var(--secondary-black);
  color: var(--text-gray);
  padding: 12px 16px;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  min-width: 760px;
`;

const TRow = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || '1fr 1fr'};
  padding: 12px 16px;
  border-top: 1px solid var(--border-color);
  color: var(--text-white);
  align-items: center;
  min-width: 760px;
  &:hover { background: #1a1a1a; }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: rgba(255, 71, 87, 0.1); 
  color: #ff4757; 
  border: 1px solid rgba(255, 71, 87, 0.3); 
  padding: 6px 12px; 
  border-radius: 6px; 
  cursor: pointer; 
  font-size: 0.8rem; 
  transition: all 0.2s; 
  &:hover { background: #ff4757; color: #fff; }
`;

const Badge = styled.span`
  background: ${props => props.role === 'admin' ? 'rgba(224, 193, 111, 0.2)' : 'rgba(255, 255, 255, 0.1)'}; 
  color: ${props => props.role === 'admin' ? '#e0c16f' : '#ccc'}; 
  padding: 4px 10px; 
  border-radius: 20px; 
  font-size: 0.75rem; 
  text-transform: capitalize;
`;
const AdminForm = styled.div`background:#111;border:1px solid #333;border-radius:16px;padding:24px;margin-bottom:24px;h2{margin-bottom:18px}.grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:14px}label{display:block;color:#aaa;font-size:.85rem;margin-bottom:6px}input,textarea{width:100%;background:#050505;color:#fff;border:1px solid #3a3a3a;border-radius:8px;padding:12px;font-size:16px}textarea{min-height:120px;resize:vertical}.check{display:flex;align-items:center;gap:8px;margin:12px 0;color:#ddd}.check input{width:auto}button{background:linear-gradient(95deg,#e0c16f,#bd851b);color:#000;border:0;border-radius:8px;padding:12px 18px;font-weight:800;cursor:pointer;margin-top:14px}`;

const AdminDashboard = () => {
  const { token, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  
  // States mapped to the new backend
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [creators, setCreators] = useState([]);
  
  // (Placeholder states for tabs we haven't built backend for yet, but keeps UI alive)
  const [transactions, setTransactions] = useState({ list: [], total: 0, page: 1, limit: 10, filters: {} });
  const [settings, setSettings] = useState(null);
  const [settingsMessage, setSettingsMessage] = useState({ type: '', text: '' });
  const [emailStatus, setEmailStatus] = useState(false);
  const [emailForm, setEmailForm] = useState({recipient:'', subject:'', message:'', allUsers:false});

  const axiosAuth = useMemo(() => {
    const instance = axios.create({ baseURL: `${getApiUrl()}` });
    instance.interceptors.request.use((config) => {
      if (token) config.headers['Authorization'] = `Bearer ${token}`; // Promenjeno na Bearer zbog tvog standardnog Auth middleware-a
      return config;
    });
    return instance;
  }, [token]);

  // Učitavanje na samom početku (Sada gađa nove rute koje smo napravili)
  useEffect(() => {
    if (!isAdmin()) return;
    fetchOverview();
  }, [axiosAuth, isAdmin]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth.get('/admin/stats');
      setOverview(res.data);
    } catch (e) {
      console.error('Admin overview fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersList = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth.get('/admin/users');
      setUsers(res.data.users || []);
    } catch (e) {
      console.error('Admin users fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreatorsList = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth.get('/admin/creators');
      setCreators(res.data.creators || []);
    } catch (e) {
      console.error('Admin creators fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await axiosAuth.get('/admin/transactions?limit=50');
      setTransactions({ list: res.data.transactions || [], total: res.data.pagination?.total || 0 });
    } catch (e) {
      console.error('Admin transactions fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    if (window.confirm("Are you sure you want to permanently delete this user?")) {
      try {
        await axiosAuth.delete(`/admin/users/${id}`);
        fetchUsersList(); // Osveži listu
      } catch (e) {
        console.error('Delete user failed', e);
      }
    }
  };

  const adminFeatures = [
    { icon: <Users />, title: "User Management", description: "Manage all users, view profiles, and handle account status." },
    { icon: <Star />, title: "Creator Directory", description: "Monitor creator growth, followers, and verification status." },
    { icon: <DollarSign />, title: "Payment Reports", description: "Monitor customer transactions and creator revenue." },
    { icon: <Shield />, title: "Platform Settings", description: "Manage public links, support email, and customer messages." }
  ];

  const currency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n || 0));

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'overview') fetchOverview();
    if (key === 'users') fetchUsersList();
    if (key === 'creators') fetchCreatorsList();
    if (key === 'transactions') fetchTransactions();
    if (key === 'settings') fetchSettings();
  };

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, emailRes] = await Promise.all([axiosAuth.get('/admin/settings'), axiosAuth.get('/admin/email/status')]);
      setSettings(settingsRes.data);
      setEmailStatus(emailRes.data.configured);
    } catch (error) { setSettingsMessage({type:'error',text:'Unable to load settings'}); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    try {
      const res = await axiosAuth.put('/admin/settings', settings);
      setSettings(res.data); setSettingsMessage({type:'success',text:'Settings saved.'});
    } catch (error) { setSettingsMessage({type:'error',text:error.response?.data?.error || 'Unable to save settings'}); }
  };

  const sendEmail = async () => {
    try {
      const res = await axiosAuth.post('/admin/email/send', emailForm);
      setSettingsMessage({type:'success',text:`Email sent to ${res.data.sent} user${res.data.sent === 1 ? '' : 's'}.`});
      setEmailForm({...emailForm, subject:'', message:''});
    } catch (error) { setSettingsMessage({type:'error',text:error.response?.data?.error || 'Unable to send email'}); }
  };

  if (!isAdmin()) return <AdminContainer><div style={{color:'red', textAlign:'center'}}>Access Denied. Admins Only.</div></AdminContainer>;

  return (
    <AdminLayout active={activeTab} onSelect={handleTabChange} title="Admin Console">
      
      {loading && activeTab === 'overview' && <div style={{textAlign:'center', padding:'20px'}}>Loading stats...</div>}

      {/* --- TAB: OVERVIEW --- */}
      {activeTab === 'overview' && overview && (
        <>
          <StatsGrid>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="stat-number">{overview.totalUsers}</div>
              <div className="stat-label">Total Users</div>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: .1 }}>
              <div className="stat-number">{overview.totalCreators}</div>
              <div className="stat-label">Total Creators</div>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: .2 }}>
              <div className="stat-number">{overview.totalRevenue}</div>
              <div className="stat-label">Est. Platform Revenue</div>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: .3 }}>
              <div className="stat-number">{overview.activeSessions}</div>
              <div className="stat-label">Active Sessions</div>
            </StatCard>
          </StatsGrid>

          <AdminGrid>
            {adminFeatures.map((feature, index) => (
              <AdminCard key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.1 }}>
                <CardHeader>
                  {feature.icon}
                  <h3>{feature.title}</h3>
                </CardHeader>
                <CardContent>{feature.description}</CardContent>
              </AdminCard>
            ))}
          </AdminGrid>
        </>
      )}

      {/* --- TAB: USERS --- */}
      {activeTab === 'users' && (
        <Section>
          {loading ? <div style={{textAlign:'center'}}>Loading users...</div> : (
            <Table>
              <THeader $cols="1fr 2fr 2fr 1fr 1fr 1fr"> 
                <div>ID</div>
                <div>Username</div>
                <div>Email</div>
                <div>Role</div>
                <div>Joined Date</div>
                <div>Actions</div>
              </THeader>
              {users.map((u) => (
                <TRow key={u.id} $cols="1fr 2fr 2fr 1fr 1fr 1fr">
                  <div style={{color: '#666'}}>#{u.id}</div>
                  <div style={{fontWeight: 'bold'}}>{u.username}</div>
                  <div style={{color: '#aaa'}}>{u.email}</div>
                  <div><Badge role={u.role}>{u.role}</Badge></div>
                  <div style={{color: '#aaa', fontSize: '0.9rem'}}>{new Date(u.created_at).toLocaleDateString()}</div>
                  <Actions>
                    {u.role !== 'admin' && (
                      <ActionButton onClick={() => deleteUser(u.id)}>Delete</ActionButton>
                    )}
                  </Actions>
                </TRow>
              ))}
            </Table>
          )}
        </Section>
      )}

      {/* --- TAB: CREATORS --- */}
      {activeTab === 'creators' && (
        <Section>
          {loading ? <div style={{textAlign:'center'}}>Loading creators...</div> : (
            <Table>
              <THeader $cols="1fr 2fr 2fr 1fr 1fr"> 
                <div>ID</div>
                <div>Display Name</div>
                <div>Username URL</div>
                <div>Followers</div>
                <div>Tier Status</div>
              </THeader>
              {creators.map((c) => (
                <TRow key={c.creator_id} $cols="1fr 2fr 2fr 1fr 1fr">
                  <div style={{color: '#666'}}>#{c.creator_id}</div>
                  <div style={{fontWeight: 'bold', color: 'var(--primary-gold)'}}>{c.display_name || c.username}</div>
                  <div style={{color: '#aaa'}}>bucx.io/{c.username}</div>
                  <div>{c.followers_count} fans</div>
                  <div>{c.is_gold ? <Badge role="admin">Gold VIP</Badge> : <Badge>Standard</Badge>}</div>
                </TRow>
              ))}
            </Table>
          )}
        </Section>
      )}

      {activeTab === 'settings' && settings && (
        <Section>
          {settingsMessage.text && <div style={{color:settingsMessage.type==='error'?'#ff8b96':'#e0c16f',marginBottom:'16px'}}>{settingsMessage.text}</div>}
          <AdminForm>
            <h2>Public contact links</h2>
            <div className="grid">
              {['discord_url','telegram_url','twitter_url','support_email'].map(field => <div key={field}><label>{field.replaceAll('_',' ')}</label><input type={field==='support_email'?'email':'url'} value={settings[field] || ''} onChange={e=>setSettings({...settings,[field]:e.target.value})}/></div>)}
            </div>
            <button onClick={saveSettings}>Save website settings</button>
          </AdminForm>
          <AdminForm>
            <h2>Email users</h2>
            <p style={{color:emailStatus?'#7bd88f':'#ffb86c'}}>{emailStatus ? 'SMTP is connected.' : 'SMTP credentials are required in the server environment before sending.'}</p>
            <label className="check"><input type="checkbox" checked={emailForm.allUsers} onChange={e=>setEmailForm({...emailForm,allUsers:e.target.checked})}/> Send to all customers</label>
            {!emailForm.allUsers && <><label>Recipient email</label><input type="email" value={emailForm.recipient} onChange={e=>setEmailForm({...emailForm,recipient:e.target.value})}/></>}
            <label style={{marginTop:'12px'}}>Subject</label><input value={emailForm.subject} onChange={e=>setEmailForm({...emailForm,subject:e.target.value})}/>
            <label style={{marginTop:'12px'}}>Message</label><textarea value={emailForm.message} onChange={e=>setEmailForm({...emailForm,message:e.target.value})}/>
            <button disabled={!emailStatus} onClick={sendEmail}>Send email</button>
          </AdminForm>
        </Section>
      )}

      {activeTab === 'transactions' && (
        <Section>
          {loading ? <div>Loading transactions…</div> : <Table>
            <THeader $cols="1fr 1.5fr 1.5fr 1fr 1fr 1.3fr"><div>ID</div><div>Customer</div><div>Creator</div><div>Amount</div><div>Status</div><div>Type</div></THeader>
            {transactions.list.map(tx => <TRow key={tx.id} $cols="1fr 1.5fr 1.5fr 1fr 1fr 1.3fr"><div>#{tx.id}</div><div>{tx.supporter_username || tx.supporter_email || 'Guest'}</div><div>{tx.creator_name || '—'}</div><div>{currency(tx.amount)}</div><div>{tx.status}</div><div>{tx.transaction_type}</div></TRow>)}
          </Table>}
        </Section>
      )}

    </AdminLayout>
  );
};

export default AdminDashboard;
